/**
 * LegacyUnitWarning Component
 * 
 * Displays a warning banner when legacy custom units are detected.
 * Provides actions to convert to standard Strict 5 units.
 * 
 * Requirements: 16.3, 16.4, 16.5
 */

import React, { useState, useEffect } from 'react';
import './LegacyUnitWarning.css';

const LegacyUnitWarning = ({ apiBaseUrl = '/api' }) => {
  const [legacyData, setLegacyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkLegacyUnits();
  }, []);

  const checkLegacyUnits = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('No authentication token found. Skipping legacy unit check.');
        return;
      }
      
      const response = await fetch(`${apiBaseUrl}/inventory/legacy-units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        console.warn('Session expired. Legacy unit check skipped.');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setLegacyData(data);
      }
    } catch (error) {
      console.error('Error checking legacy units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in localStorage (expires after session)
    sessionStorage.setItem('legacy-units-dismissed', 'true');
  };

  const handleViewDetails = () => {
    setShowDetails(!showDetails);
  };

  // Don't show if loading, dismissed, or no legacy units
  if (loading || dismissed || !legacyData?.hasLegacyUnits) {
    return null;
  }

  // Check if already dismissed in this session
  if (sessionStorage.getItem('legacy-units-dismissed')) {
    return null;
  }

  return (
    <div className="legacy-unit-warning">
      <div className="warning-header">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          <h4>Legacy Custom Units Detected</h4>
          <p>
            Your inventory contains <strong>{legacyData.count}</strong> item(s) using legacy custom units. 
            These units are no longer supported. Please convert them to standard units (kg, g, L, ml, pcs) 
            to ensure accurate inventory tracking.
          </p>
        </div>
        <button className="dismiss-btn" onClick={handleDismiss} title="Dismiss">
          ×
        </button>
      </div>

      <div className="warning-actions">
        <button 
          className="btn btn-primary btn-sm"
          onClick={handleViewDetails}
        >
          {showDetails ? 'Hide Details' : 'View Affected Items'}
        </button>
        <a 
          href="/inventory" 
          className="btn btn-success btn-sm"
        >
          Convert to Standard Units →
        </a>
      </div>

      {showDetails && (
        <div className="legacy-items-list">
          <h5>Items Requiring Conversion:</h5>
          <div className="items-table-wrapper">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Current Unit</th>
                  <th>Unit ID</th>
                </tr>
              </thead>
              <tbody>
                {legacyData.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.unit_name || 'Unknown'}</td>
                    <td>{item.unit_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="migration-note">
            <strong>How to convert:</strong> Go to Inventory page, select an item, and change its unit 
            to one of the standard units: kg, g, L, ml, or pcs. The system will guide you through 
            the conversion process and update all related recipes automatically.
          </div>
        </div>
      )}
    </div>
  );
};

export default LegacyUnitWarning;
